import { Link } from "react-router-dom";
import {
  ArrowRight, Phone, Wallet, ShieldCheck, Zap, CheckCircle2, Sparkles, ChevronRight,
} from "lucide-react";
import SEO from "@/components/site/SEO";
import Reveal from "@/components/site/Reveal";
import CallingDialer from "@/components/customer/CallingDialer";
import HomeDestinations from "@/components/site/HomeDestinations";
import HomeTestimonials from "@/components/site/HomeTestimonials";

const REASONS = [
  "Per-second billing",
  "No connection fees",
  "No hidden charges",
  "UK-regulated wallet",
  "Crystal-clear audio",
  "50+ African destinations",
];

const ORG_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "OneDial Africa",
  legalName: "Scitygate Telecom UK Ltd",
  url: "https://www.onedialafrica.com",
  logo: "https://www.onedialafrica.com/assets/brand/logo-wordmark.png",
  telephone: "+44 161 989 4842",
  email: "support@onedialafrica.com",
  address: {
    "@type": "PostalAddress",
    streetAddress: "2nd Floor, Halifax House, 93–101 Bridge Street",
    addressLocality: "Manchester",
    postalCode: "M3 2GX",
    addressCountry: "GB",
  },
};

const HOW = [
  {
    n: "1", title: "Top up your wallet",
    desc: "Add £5 in seconds with any UK or international card. GBP, closed-loop, regulated.",
  },
  {
    n: "2", title: "Choose a destination",
    desc: "Pick a country, dial a number or tap a saved contact. See the per-minute rate live.",
  },
  {
    n: "3", title: "Tap call",
    desc: "Crystal-clear audio over our hybrid carrier network. Billed by the second.",
  },
];

// Preferred order for the rates preview – the eight markets we lead with.
const PREFERRED_RATES_ORDER = ["GB", "NG", "GH", "KE", "UG", "ZA", "US", "CA"];
// (Kept for backward compat — no longer used since HomeDestinations owns the section.)
void PREFERRED_RATES_ORDER;

export default function Home() {
  return (
    <div data-testid="home-page">
      <SEO
        title="Call Africa from anywhere · Crystal-clear, per-second billing"
        description="OneDial Africa — call Africa from anywhere in the world. Crystal-clear calls. Per-second billing. No hidden fees."
        path="/"
        schema={ORG_SCHEMA}
      />

      {/* ════════════════════════ HERO — DIALER IS THE PRODUCT ═══════════════════════ */}
      <section
        id="dialer"
        className="relative overflow-hidden bg-hero-gradient text-white"
        data-testid="hero-section"
      >
        <div className="absolute inset-0 bg-grid-overlay opacity-50" aria-hidden />
        <div className="pointer-events-none absolute -top-40 -left-32 h-[28rem] w-[28rem] bg-brand-green/15 blur-3xl rounded-full" aria-hidden />
        <div className="pointer-events-none absolute top-1/3 -right-40 h-[32rem] w-[32rem] bg-brand-inkSoft/40 blur-3xl rounded-full" aria-hidden />

        <div className="relative container-page pt-4 pb-10 lg:pt-10 lg:pb-16 grid lg:grid-cols-12 gap-8 lg:gap-10 items-center">
          {/* COMPACT VALUE-PROP — 2 lines only, per spec */}
          <div className="lg:col-span-4 order-2 lg:order-1 lg:py-6 min-w-0">
            <Reveal>
              <h1 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl xl:text-[56px] leading-[1.04] tracking-tight">
                Call Africa from <span className="text-gradient-green">anywhere</span>.
              </h1>
            </Reveal>
            <Reveal delay={0.05}>
              <p className="mt-4 text-base lg:text-lg text-white/75 leading-relaxed">
                Per-second billing. No hidden fees.
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link to="/register" data-testid="hero-register-cta"
                  className="inline-flex items-center gap-2 rounded-full bg-brand-green hover:bg-brand-greenHover text-white px-5 py-2.5 text-sm font-bold">
                  Get £1 free credit
                </Link>
                <Link to="/login" data-testid="hero-signin-cta"
                  className="inline-flex items-center gap-2 rounded-full border border-white/20 hover:bg-white/5 px-5 py-2.5 text-sm font-semibold text-white">
                  Sign in <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </div>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="hidden lg:block mt-6 text-[11px] text-white/45 max-w-xs">
                UK-licensed telecom · Scitygate Telecom UK Ltd · Co. No. 16468736
              </p>
            </Reveal>
          </div>

          {/* DIALER — THE PRODUCT (larger card, takes 8/12 on desktop) */}
          <div className="lg:col-span-8 order-1 lg:order-2 min-w-0 w-full" data-testid="hero-dialer-wrap">
            <Reveal delay={0.05}>
              <div className="w-full rounded-[28px] bg-white p-3 sm:p-5 md:p-6 lg:p-8 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.5)] ring-1 ring-black/5 max-w-md lg:max-w-xl mx-auto">
                <CallingDialer guestPreview size="lg" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════ DESTINATIONS ════════════════════════════════ */}
      <HomeDestinations />

      {/* ════════════════════════════════ HOW IT WORKS ═════════════════════════════════ */}
      <section className="bg-white" data-testid="how-it-works-section">
        <div className="container-page py-16 lg:py-24">
          <Reveal>
            <div className="text-center max-w-2xl mx-auto">
              <p className="label-eyebrow text-brand-green">How it works</p>
              <h2 className="mt-3 font-display font-black text-3xl sm:text-4xl lg:text-5xl text-brand-ink leading-[1.05]">
                From zero to first call in <span className="text-brand-green">under a minute.</span>
              </h2>
            </div>
          </Reveal>
          <div className="mt-12 grid md:grid-cols-3 gap-5">
            {HOW.map((h, i) => (
              <Reveal key={h.n} delay={i * 0.05}>
                <article
                  data-testid={`how-step-${h.n}`}
                  className="relative h-full rounded-2xl border border-brand-line bg-white p-6 hover:shadow-xl hover:-translate-y-1 transition-all"
                >
                  <div className="absolute -top-4 left-6 h-9 w-9 rounded-full bg-brand-green text-white font-display font-black flex items-center justify-center shadow-lg">
                    {h.n}
                  </div>
                  <h3 className="mt-3 font-display font-bold text-lg text-brand-ink">{h.title}</h3>
                  <p className="mt-2 text-sm text-brand-muted leading-relaxed">{h.desc}</p>
                </article>
              </Reveal>
            ))}
          </div>
          <div className="mt-10 text-center">
            <a href="#dialer" data-testid="how-cta"
              className="inline-flex items-center gap-2 rounded-full bg-brand-green hover:bg-brand-greenHover text-white px-6 py-3 text-sm font-bold">
              <Phone className="h-4 w-4 fill-current" /> Try the dialer now
            </a>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════ WHY ONEDIAL AFRICA ════════════════════════════════ */}
      <section className="relative overflow-hidden bg-mesh-dark text-white" data-testid="why-section">
        <div className="absolute inset-0 bg-grid-overlay opacity-50" aria-hidden />
        <div className="relative container-page py-20 lg:py-28 grid lg:grid-cols-12 gap-12 items-center">
          <Reveal className="lg:col-span-5">
            <p className="label-eyebrow text-brand-green">Why OneDial Africa</p>
            <h2 className="mt-3 font-display font-black text-3xl sm:text-4xl lg:text-5xl text-white leading-[1.05]">
              The fairest way<br /> to call home.
            </h2>
            <p className="mt-5 max-w-md text-white/65 leading-relaxed">
              No connection fees. No hidden charges. No surprises. Just a transparent
              telecom built for families across the diaspora.
            </p>
            <div className="mt-7 inline-flex items-center gap-3 rounded-2xl bg-white/5 ring-1 ring-white/10 backdrop-blur px-4 py-3">
              <Sparkles className="h-5 w-5 text-brand-green" />
              <div className="text-xs text-white/85 leading-tight">
                <p className="font-semibold">Average customer saves up to 80%</p>
                <p className="text-white/55">vs traditional carriers on international calls</p>
              </div>
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href="#dialer" data-testid="why-cta-dial"
                className="inline-flex items-center gap-2 rounded-full bg-brand-green hover:bg-brand-greenHover text-white px-5 py-2.5 text-sm font-bold">
                <Phone className="h-4 w-4 fill-current" /> Start calling
              </a>
              <Link to="/rates" data-testid="why-cta-rates"
                className="inline-flex items-center gap-2 rounded-full border border-white/20 hover:bg-white/5 px-5 py-2.5 text-sm font-semibold text-white">
                See all rates <ChevronRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
          <Reveal className="lg:col-span-7" delay={0.1}>
            <div className="rounded-3xl bg-white/5 ring-1 ring-white/10 backdrop-blur p-6 sm:p-8">
              <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
                {REASONS.map((r, i) => (
                  <div key={r} data-testid={`reason-${i}`}
                    className="flex items-center gap-3 rounded-xl border border-white/10 bg-brand-ink/40 px-4 py-3"
                  >
                    <span className="h-7 w-7 rounded-full bg-brand-green text-white flex items-center justify-center shrink-0">
                      <CheckCircle2 className="h-4 w-4" />
                    </span>
                    <span className="text-sm font-semibold text-white">{r}</span>
                  </div>
                ))}
              </div>
              <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Mini icon={Zap} label="Per-second" />
                <Mini icon={ShieldCheck} label="Encrypted" />
                <Mini icon={Wallet} label="GBP wallet" />
                <Mini icon={Phone} label="50+ countries" />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ════════════════════════════════ TESTIMONIALS ═════════════════════════════════ */}
      <HomeTestimonials />
    </div>
  );
}

function Mini({ icon: Icon, label }) {
  return (
    <div className="rounded-xl border border-white/10 bg-brand-ink/40 px-3 py-3 flex items-center gap-2 text-xs text-white/90">
      <Icon className="h-4 w-4 text-brand-green" />
      <span className="font-semibold">{label}</span>
    </div>
  );
}
